/**
 * Candidate outreach — API client for the staff candidate calling campaign.
 * Same shape as `lib/winback/api.ts`. Backend: app/api/routes/candidate_campaign.py.
 */
import { fetchWithAuth, API_BASE } from "@/lib/auth";
import { saveCsvResponse, type OutcomeDef, type OutreachEntryBase, type StaffOption } from "@/lib/campaigns/types";
import { serializeFilters, type FilterValues } from "@/lib/campaigns/urlFilters";

const BASE = `${API_BASE}/api/v1/staff/candidate-campaign`;

async function req(path: string, options: RequestInit = {}) {
  const res = await fetchWithAuth(`${BASE}${path}`, {
    ...options,
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const detail = typeof data.detail === "string" ? data.detail : `Request failed (${res.status})`;
    throw Object.assign(new Error(detail), { status: res.status });
  }
  return res.json();
}

// ── Vocabulary (mirrors backend app/candidate_campaign/) ────────────────────

export type OutcomeKey =
  | "scheduled_exam" | "will_schedule" | "prometric_assistance" | "considering" | "will_not_schedule"
  | "plans_to_cancel" | "plans_to_defer" | "traveling_on_exam_date" | "curriculum_concern" | "technical_issue"
  | "accommodation_request" | "voicemail" | "no_answer" | "invalid_contact" | "call_back" | "not_interested"
  | "other" | "follow_up"
  // Retired 2026-10-09: still on calls logged before then.
  | "will_register" | "needs_support" | "not_this_cycle" | "emailed" | "do_not_contact";

/** Headings the outcome picker groups the choices under. */
export const OUTCOME_GROUPS: Record<string, string> = {
  scheduling: "Scheduling", barrier: "Barriers", contact: "Reaching them", other: "Other",
};

/** In the order the picker shows them. A call can carry several. */
export const OUTCOMES: OutcomeDef<OutcomeKey>[] = [
  { key: "scheduled_exam", label: "Scheduled exam", short: "Scheduled", tone: "emerald", group: "scheduling" },
  { key: "will_schedule", label: "Will schedule", short: "Will schedule", tone: "emerald", group: "scheduling" },
  { key: "prometric_assistance", label: "Prometric scheduling assistance required", short: "Needs Prometric help", tone: "violet", group: "scheduling" },
  { key: "considering", label: "Considering scheduling", short: "Considering", tone: "steel", group: "scheduling" },
  { key: "will_not_schedule", label: "Will not schedule", short: "Won't schedule", tone: "red", group: "scheduling" },
  { key: "plans_to_cancel", label: "Plans to cancel", short: "Plans to cancel", tone: "red", group: "scheduling" },
  { key: "plans_to_defer", label: "Plans to defer", short: "Plans to defer", tone: "amber", group: "scheduling" },
  { key: "traveling_on_exam_date", label: "Traveling on exam date", short: "Traveling", tone: "amber", group: "barrier" },
  { key: "curriculum_concern", label: "Curriculum/exam concern", short: "Exam concern", tone: "violet", group: "barrier" },
  { key: "technical_issue", label: "Technical issue", short: "Technical issue", tone: "violet", group: "barrier" },
  { key: "accommodation_request", label: "Accommodation request", short: "Accommodation", tone: "violet", group: "barrier" },
  { key: "voicemail", label: "Voicemail left", short: "Voicemail", tone: "amber", group: "contact" },
  { key: "no_answer", label: "No answer", short: "No answer", tone: "amber", group: "contact" },
  { key: "invalid_contact", label: "Wrong number", short: "Wrong number", tone: "red", group: "contact" },
  { key: "call_back", label: "Call back requested", short: "Call back", tone: "steel", group: "contact" },
  { key: "not_interested", label: "Not interested in pursuing CMT", short: "Not interested", tone: "slate", group: "other" },
  { key: "other", label: "Other", short: "Other", tone: "slate", group: "other", needsNote: true },
  { key: "follow_up", label: "Follow up required", short: "Follow up", tone: "steel", group: "other" },
  { key: "will_register", label: "Spoke – will register", short: "Will register", tone: "emerald", retired: true },
  { key: "needs_support", label: "Needs help from staff", short: "Needs help", tone: "violet", retired: true },
  { key: "not_this_cycle", label: "Not this cycle", short: "Not this cycle", tone: "red", retired: true },
  { key: "emailed", label: "Emailed", short: "Emailed", tone: "steel", retired: true },
  { key: "do_not_contact", label: "Do not contact", short: "Do not contact", tone: "slate", retired: true },
];

/** What the cross-check found about a candidate on the list. */
export type FlagKey = "no_eligibility" | "level_mismatch";
export const FLAGS: { value: FlagKey; label: string; hint: string }[] = [
  { value: "no_eligibility", label: "No eligibility at Prometric",
    hint: "Registered and paid in myCMT, but Prometric has no eligibility for them, so they can't book yet" },
  { value: "level_mismatch", label: "Level differs at Prometric",
    hint: "Prometric's eligibility is for a different level than their myCMT registration" },
];

/** The one list: registered candidates who haven't booked, from the Prometric sync. */
export type SegmentKey = "registered_unscheduled";

export type StatusKey = "not_contacted" | "contacted" | "converted";
export const STATUSES: { value: StatusKey; label: string }[] = [
  { value: "not_contacted", label: "Not contacted this cycle" },
  { value: "contacted", label: "Contacted" },
  { value: "converted", label: "Converted after outreach" },
];

export const LEVELS: { value: string; label: string; short: string }[] = [
  { value: "1", label: "Level I", short: "L1" },
  { value: "2", label: "Level II", short: "L2" },
  { value: "3", label: "Level III", short: "L3" },
];
export const levelLabel = (n: number | null | undefined) => (n ? LEVELS[n - 1]?.label ?? `Level ${n}` : "—");

export const MEMBERSHIP = [
  { value: "member", label: "Active member" },
  { value: "non_member", label: "Not a member" },
];
export const PAYMENTS = [
  { value: "paid", label: "Paid" },
  { value: "no-cost-order", label: "No-cost order" },
  { value: "partially-refunded", label: "Partially refunded" },
];
export const ATTEMPTS = [
  { value: "0", label: "Never contacted" },
  { value: "1", label: "Once" },
  { value: "2plus", label: "Twice or more" },
];
export type SortKey = "name" | "level" | "registered" | "recent_contact" | "region";

// ── Filters: one spec for the URL and the API (keys are the backend's params) ──

// The campaign works the current cycle only, so the cycle isn't a filter.
export const CANDIDATE_SPEC = {
  level: "list", region: "list", country: "list",
  status: "list", outcome: "list", flag: "list", contacted_by: "list", attempts: "string", has_phone: "bool",
  membership: "list", payment: "list",
  registered_from: "string", registered_to: "string", q: "string", sort: "string",
} as const;

export type CandidateFilters = FilterValues<typeof CANDIDATE_SPEC>;

export const CANDIDATE_DEFAULTS: CandidateFilters = {
  level: [], region: [], country: [], status: [], outcome: [], flag: [], contacted_by: [],
  attempts: null, has_phone: false, membership: [], payment: [],
  registered_from: null, registered_to: null, q: null, sort: "name",
};

// ── Shapes ──────────────────────────────────────────────────────────────────

export interface CycleInfo {
  value: string;
  registered: number;
  scheduled: number;
  when: "past" | "current" | "future";
}

export interface CandidateRow {
  member_id: number;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  country: string | null;
  region: string;
  /** From the candidate's myCMT address (or the list row when myCMT has none). */
  city: string | null;
  state: string | null;
  flags: FlagKey[];
  segment: SegmentKey;
  level: number | null;
  cmt_i_status: string | null;
  cmt_ii_status: string | null;
  cmt_iii_status: string | null;
  payment_status: string | null;
  registered_on: string | null;
  is_member: boolean;
  tier: string | null;
  prometric: boolean;
  scheduled_date: string | null;
  status: StatusKey;
  converted: boolean;
  converted_to: "scheduled" | null;
  latest_outcome: OutcomeKey | null;
  /** Every outcome of the latest call; `latest_outcome` is the first. */
  latest_outcomes: OutcomeKey[] | null;
  latest_at: string | null;
  latest_by: string | null;
  outreach_count: number;
  /** Not in the CRM yet — known only from the list row. */
  in_crm: boolean;
  /** On the list version in use for the cycle. */
  on_list: boolean;
  remark: string | null;
  /** Nearest active Hub community and the geo pipeline's nearest centre city. */
  community: string | null;
  community_mi: number | null;
  centre: string | null;
  /** Nearest active Prometric test centre to their geo point. */
  test_centre: string | null;
  test_centre_city: string | null;
  test_centre_mi: number | null;
}

interface Counts { total: number; not_contacted: number; contacted: number; converted: number; matching: number }

export interface CandidateSummary extends Counts {
  conversion_rate: number | null;
  regions: ({ region: string } & Counts)[];
  levels: ({ level: number } & Counts)[];
  countries: { country: string; region: string | null; count: number; total: number }[];
}

/** The list version in use for the cycle. */
export interface ListSource {
  id: string;
  cycle: string;
  filename: string | null;
  rows: number;
  uploaded_at: string;
  uploaded_by: string | null;
  meta: ImportMeta;
  /** "auto" = written by the Prometric sync; "upload" = a staff upload from before it. */
  source?: "auto" | "upload";
  /** How many versions this cycle has had, undone ones included. */
  uploads?: number;
}

export interface ImportMeta {
  added?: number | null;
  dropped?: number | null;
  dropped_with_calls?: number;
  not_in_crm?: number;
  not_registered_in_crm?: number;
  already_booked?: number;
  max_payment_date?: string | null;
  warnings?: string[];
}

/** One list version in the history. */
export interface ImportInfo {
  id: string;
  cycle: string;
  filename: string | null;
  rows: number;
  uploaded_at: string;
  uploaded_by: string | null;
  withdrawn_at: string | null;
  withdrawn_by: string | null;
  in_use: boolean;
  meta: ImportMeta;
  source?: "auto" | "upload";
}

export interface UploadWarning {
  code: "large_drop" | "older_report" | "unchanged" | "many_unknown" | "already_booked";
  /** True when the result could lose work and needs an explicit confirm. */
  confirm: boolean;
  message: string;
}

export type IssueKey = "no_eligibility" | "level_mismatch" | "prometric_only" | "schedule_disagreement" | "portal_unscheduled_diff";

export interface LevelCheck {
  registered: number;
  eligible: number;
  scheduled_gee: number;
  scheduled_portal: number;
  to_call: number;
  no_eligibility: number;
}

/** The cross-check of Prometric GEE against the CMT portal, as of one sync. */
export interface SyncSummary {
  cycle?: string;
  registered?: number;
  eligible?: number;
  scheduled_gee?: number;
  scheduled_portal?: number;
  to_call?: number;
  levels?: Record<string, LevelCheck>;
  issues?: Partial<Record<IssueKey, { count: number; portal_only?: number; ours_only?: number }>>;
  warnings?: UploadWarning[];
  preview?: { added: number | null; dropped: number | null; dropped_with_calls: number };
}

export type SyncStatus = "running" | "ok" | "unchanged" | "needs_confirmation" | "failed";

export interface SyncRun {
  id: string;
  cycle: string | null;
  trigger: "schedule" | "staff";
  status: SyncStatus;
  started_at: string;
  finished_at: string | null;
  started_by: string | null;
  import_id: string | null;
  summary: SyncSummary;
  error: string | null;
}

export interface SyncState {
  /** False when the server has no Prometric GEE credentials. */
  configured: boolean;
  running: SyncRun | null;
  last: SyncRun | null;
  last_success: SyncRun | null;
}

export interface IssueMember {
  member_id: number;
  name: string | null;
  email: string | null;
  registration_status: string | null;
  administration: string | null;
  registering_for_level: string | null;
  prometric: string | null;
}

export interface CandidatePage {
  cycle: string;
  current_cycle: string;
  rows: CandidateRow[];
  total: number;
  summary: CandidateSummary;
  list_source?: ListSource | null;
  sync?: SyncState;
  as_of: string;
}

export interface OutreachEntry extends OutreachEntryBase<OutcomeKey> {
  cycle: string;
  segment: SegmentKey;
  level: number | null;
}

export interface CandidateCard {
  member_id: number;
  first_name: string | null;
  last_name: string | null;
  email_1: string | null;
  email_2: string | null;
  email_3: string | null;
  phone_1: string | null;
  phone_2: string | null;
  phone_3: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  region: string;
  tier: string | null;
  program_journey: string | null;
  cmt_i_status: string | null;
  cmt_i_taken_date: string | null;
  cmt_ii_status: string | null;
  cmt_ii_taken_date: string | null;
  cmt_iii_status: string | null;
  cmt_iii_taken_date: string | null;
  last_cycle: string | null;
  registration_status: string | null;
  registering_for_level: string | null;
  payment_status: string | null;
  payment_date: string | null;
  user_id: string | null;
  chapter: string | null;
  job_title: string | null;
  company: string | null;
  linkedin: string | null;
}

export interface CandidateDetail {
  candidate: CandidateCard & {
    not_in_crm?: boolean; community?: string | null; community_mi?: number | null; centre?: string | null;
    test_centre?: string | null; test_centre_city?: string | null; test_centre_mi?: number | null;
  };
  name: string;
  cycle: string;
  campaign: {
    segment: SegmentKey; level: number | null; status: StatusKey; converted: boolean;
    converted_to: "scheduled" | null; remark: string | null; on_list: boolean; flags?: FlagKey[];
  } | null;
  prometric: { cycle: string; level: string | null; prometric_status: string | null; scheduled_date: string | null; test_center: string | null; place: string | null }[];
  /** Prometric GEE's eligibility record for this cycle, from the last sync; null = never synced. */
  eligibility?: { exam_code: string; level: number | null; start_date: string | null; end_date: string | null; schedule_date: string | null; seen_at: string }[] | null;
  outreach: OutreachEntry[];
}

export function queryString(f: CandidateFilters, extra: Record<string, string | number> = {}): string {
  const p = serializeFilters(CANDIDATE_SPEC, CANDIDATE_DEFAULTS, { ...f, q: f.q?.trim() || null });
  for (const [k, v] of Object.entries(extra)) p.set(k, String(v));
  return p.toString();
}

// ── Endpoints ───────────────────────────────────────────────────────────────

export const getCycles = (): Promise<{ current: string; cycles: CycleInfo[] }> => req("/cycles");

export const getFacets = (): Promise<{ cycle: string; contacted_by: StaffOption[]; regions: string[] }> => req("/facets");

/** Where the sync stands: a live run, the last finished one, the last that saved a list. */
export const getSyncState = (): Promise<SyncState> => req("/prometric-sync");

/** Sync now: pull Prometric GEE + the CMT portal and rebuild the list. Poll `getSyncState`. */
export const startSync = (): Promise<SyncState> => req("/prometric-sync", { method: "POST" });

/** Apply a result the sync held back (most of the list would leave, or it looks older). */
export const confirmSync = (runId: string): Promise<SyncState> => req(`/prometric-sync/${runId}/confirm`, { method: "POST" });

/** Who is in one issue bucket of the newest sync. */
export const getSyncIssue = (issue: IssueKey): Promise<{ issue: IssueKey; count: number; cycle?: string; members: IssueMember[] }> =>
  req(`/prometric-sync/issues/${issue}`);

export const listImports = (): Promise<ImportInfo[]> => req("/prometric-imports");

/** Undo a list version: it's kept, and the previous one takes over. */
export const withdrawImport = (id: string): Promise<{ import: ImportInfo; list_source: ListSource | null }> =>
  req(`/prometric-imports/${id}/withdraw`, { method: "POST" });

export const restoreImport = (id: string): Promise<{ import: ImportInfo; list_source: ListSource | null }> =>
  req(`/prometric-imports/${id}/restore`, { method: "POST" });

export const listCandidates = (f: CandidateFilters, limit: number, offset: number): Promise<CandidatePage> =>
  req(`/candidates?${queryString(f, { limit, offset })}`);

export const getCandidate = (memberId: number): Promise<CandidateDetail> => req(`/candidates/${memberId}`);

export const logOutreach = (memberId: number, outcomes: OutcomeKey[], note: string): Promise<OutreachEntry> =>
  req(`/candidates/${memberId}/outreach`, { method: "POST", body: JSON.stringify({ outcomes, note }) });

export const updateOutreach = (id: string, patch: { outcomes?: OutcomeKey[]; note?: string }): Promise<OutreachEntry> =>
  req(`/outreach/${id}`, { method: "PATCH", body: JSON.stringify(patch) });

/** Downloads the current view as CSV. The server audits every export. */
export async function downloadCsv(f: CandidateFilters): Promise<void> {
  await saveCsvResponse(await fetchWithAuth(`${BASE}/export.csv?${queryString(f)}`), "candidates.csv");
}
